import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FiChevronDown, FiChevronUp, FiMove, FiTrash2 } from 'react-icons/fi';
import LocationSelector from './LocationSelector';
import TimeDropdownInput from './TimeDropdownInput';
import styles from './StopsTable.module.css';

const formatDateSummary = (date, start, end, timezone) => {
  if (!date || (!start && !end)) return 'Date TBD';
  const formattedDate = new Date(date).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const range = [start, end].filter(Boolean).join(' – ');
  return `${formattedDate}${range ? ` · ${range}` : ''}${timezone ? ` (${timezone})` : ''}`;
};

const getLocationName = (location) => location?.name || location?.company_name || 'No location selected';
const getLocationMeta = (location) => {
  if (!location) return '';
  const cityState = [location.city, location.state].filter(Boolean).join(', ');
  const cityStateZip = cityState ? `${cityState}${location.zip ? ` ${location.zip}` : ''}` : location.zip || '';
  const street = location.street || location.address;
  return [street, cityStateZip].filter(Boolean).join(', ');
};

const getContactSummary = (location) => {
  if (!location?.contacts || location.contacts.length === 0) return '';
  const primary = location.contacts[0];
  const parts = [];
  if (primary.phone) parts.push(primary.ext ? `${primary.phone} x${primary.ext}` : primary.phone);
  if (primary.email) parts.push(primary.email);
  return parts.join(' · ');
};

const StopsTable = ({
  stops,
  onChangeStop,
  onReorderStops,
  onRemoveStop,
  onLocationChange,
  onTimeBlur,
}) => {
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [locationOpenSignal, setLocationOpenSignal] = useState({});

  useEffect(() => {
    if (stops.length) {
      const newest = stops[stops.length - 1];
      setExpandedIds((prev) => {
        if (prev.has(newest.clientId)) return prev;
        const next = new Set(prev);
        next.add(newest.clientId);
        return next;
      });
    }
  }, [stops.length, stops]);

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleDragStart = (index) => setDraggingIndex(index);

  const handleDrop = (index) => {
    if (draggingIndex === null || draggingIndex === index) {
      setDraggingIndex(null);
      return;
    }
    const reordered = [...stops];
    const [moved] = reordered.splice(draggingIndex, 1);
    reordered.splice(index, 0, moved);
    setDraggingIndex(null);
    onReorderStops(reordered);
  };

  const timeOptions = useMemo(() => {
    const options = [];
    for (let hour = 0; hour < 24; hour += 1) {
      ['00', '30'].forEach((minute) => {
        options.push(`${String(hour).padStart(2, '0')}:${minute}`);
      });
    }
    return options;
  }, []);

  return (
    <div className={styles.tableWrapper}>
      <div className={styles.headerRow}>
        <span>#</span>
        <span>Type</span>
        <span>Location</span>
        <span>Date &amp; time</span>
        <span>Scheduling Type</span>
        <span>Status</span>
        <span />
      </div>
      <div>
        {stops.map((stop, index) => {
          const isExpanded = expandedIds.has(stop.clientId);
          const locationMeta = getLocationMeta(stop.location);
          const contactMeta = getContactSummary(stop.location);
          const dateSummary = formatDateSummary(stop.date, stop.windowStart, stop.windowEnd, stop.timezone);
          return (
            <div key={stop.clientId}>
              <div
                className={`${styles.bodyRow} ${isExpanded ? styles.bodyRowExpanded : ''}`}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => handleDrop(index)}
                onClick={() => toggleExpand(stop.clientId)}
              >
                <div className={styles.grip} title="Drag to reorder">
                  <FiMove />
                  <span>{index + 1}</span>
                </div>
                <select
                  className={styles.stopTypeSelect}
                  value={stop.type}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) => onChangeStop(index, { type: event.target.value })}
                >
                  <option value="Pickup">Pickup</option>
                  <option value="Delivery">Delivery</option>
                  <option value="Transload">Transload</option>
                  <option value="Storage">Storage</option>
                </select>
                <div className={styles.locationCell}>
                  <div className={styles.locationStack}>
                    <span className={styles.locationName}>
                      {stop.location ? getLocationName(stop.location) : 'No location selected'}
                    </span>
                    <span className={styles.locationMeta}>
                      {stop.location ? locationMeta || 'Select a saved location' : 'Select a saved location'}
                    </span>
                    {contactMeta && <span className={styles.contactSummary}>{contactMeta}</span>}
                  </div>
                  <button
                    type="button"
                    className={styles.selectLink}
                    onClick={(event) => {
                      event.stopPropagation();
                      setLocationOpenSignal({ id: stop.clientId, token: Date.now() });
                    }}
                  >
                    Select location
                  </button>
                </div>
                <div>
                  <div className={styles.dateSummary}>{dateSummary}</div>
                  {stop.timezone && <div className={styles.dateSubtle}>{stop.timezone}</div>}
                </div>
                <select
                  className={styles.schedulingSelect}
                  value={stop.appointmentType}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) => {
                    const appointmentType = event.target.value;
                    onChangeStop(index, {
                      appointmentType,
                      needsAppointmentScheduled:
                        appointmentType === 'Appointment' ? stop.needsAppointmentScheduled : false,
                    });
                  }}
                >
                  <option value="">Select…</option>
                  <option value="FCFS">FCFS</option>
                  <option value="Appointment">Appointment</option>
                </select>
                <div className={styles.statusBadge}>—</div>
                <div className={styles.inlineActions}>
                  <button
                    type="button"
                    className={styles.removeButton}
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemoveStop(index);
                    }}
                    aria-label="Remove stop"
                  >
                    <FiTrash2 />
                  </button>
                  <button
                    type="button"
                    className={styles.expandButton}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleExpand(stop.clientId);
                    }}
                    aria-label={isExpanded ? 'Collapse stop details' : 'Expand stop details'}
                  >
                    {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                  </button>
                </div>
              </div>
              {isExpanded && (
                <div className={styles.detailRow}>
                  <div className={styles.detailContent}>
                    <div className={styles.detailGridThree}>
                      <div className={styles.underlinedField}>
                        <span className={styles.fieldLabel}>Location</span>
                        <div className={styles.underlinedInput}>{getLocationName(stop.location)}</div>
                        <button
                          type="button"
                          className={styles.selectLink}
                          onClick={(event) => {
                            event.stopPropagation();
                            setLocationOpenSignal({ id: stop.clientId, token: Date.now() });
                          }}
                        >
                          Select location
                        </button>
                        <LocationSelector
                          label=""
                          value={stop.location}
                          onChange={(location) => onLocationChange(index, location)}
                          renderTrigger={false}
                          openSignal={locationOpenSignal.id === stop.clientId ? locationOpenSignal.token : null}
                        />
                      </div>
                      <label className={styles.label}>
                        Date
                        <input
                          type="date"
                          value={stop.date}
                          onChange={(event) => onChangeStop(index, { date: event.target.value })}
                        />
                      </label>
                      <div className={styles.label}>
                        <span>Scheduling Type</span>
                        <select
                          value={stop.appointmentType}
                          onChange={(event) => {
                            const appointmentType = event.target.value;
                            onChangeStop(index, {
                              appointmentType,
                              needsAppointmentScheduled:
                                appointmentType === 'Appointment' ? stop.needsAppointmentScheduled : false,
                            });
                          }}
                        >
                          <option value="">Select…</option>
                          <option value="FCFS">FCFS</option>
                          <option value="Appointment">Appointment</option>
                        </select>
                        {stop.appointmentType === 'Appointment' && (
                          <label className={styles.subCheckbox}>
                            <input
                              type="checkbox"
                              checked={stop.needsAppointmentScheduled}
                              onChange={(event) =>
                                onChangeStop(index, { needsAppointmentScheduled: event.target.checked })
                              }
                            />
                            <span>Needs appointment scheduled</span>
                            <span className={styles.infoTooltip} title="Select if appointment is needed, but is yet to be requested">
                              ⓘ
                            </span>
                          </label>
                        )}
                      </div>
                    </div>

                    <div className={styles.detailGridTwo}>
                      <label className={styles.label}>
                        Window start
                        <TimeDropdownInput
                          value={stop.windowStart}
                          onChange={(value) => onChangeStop(index, { windowStart: value })}
                          onSelect={(value) => onTimeBlur(index, 'windowStart', value)}
                          onBlur={(value) => onTimeBlur(index, 'windowStart', value)}
                          options={timeOptions}
                          placeholder="HH:mm"
                        />
                      </label>
                      <label className={styles.label}>
                        Window end
                        <TimeDropdownInput
                          value={stop.windowEnd}
                          onChange={(value) => onChangeStop(index, { windowEnd: value })}
                          onSelect={(value) => onTimeBlur(index, 'windowEnd', value)}
                          onBlur={(value) => onTimeBlur(index, 'windowEnd', value)}
                          options={timeOptions}
                          placeholder="HH:mm"
                        />
                      </label>
                    </div>

                    <div className={styles.detailGridTwo}>
                      <label className={styles.label}>
                        {stop.type === 'Delivery' ? 'Delivery number (optional)' : 'Pickup number (optional)'}
                        <input
                          value={stop.reference}
                          onChange={(event) => onChangeStop(index, { reference: event.target.value })}
                        />
                      </label>
                      <label className={styles.label}>
                        Appointment number (optional)
                        <input
                          value={stop.appointmentNumber}
                          onChange={(event) => onChangeStop(index, { appointmentNumber: event.target.value })}
                        />
                      </label>
                    </div>

                    <label className={styles.label}>
                      Route notes
                      <textarea
                        rows={3}
                        value={stop.notes}
                        onChange={(event) => onChangeStop(index, { notes: event.target.value })}
                        placeholder="Driver call outs, security check-in, or document reminders"
                      />
                    </label>

                    {contactMeta && (
                      <div className={styles.contactsRow}>
                        <span className={styles.labelHeader}>Contacts</span>
                        <div className={styles.contactSummaryLine}>
                          <span>{contactMeta}</span>
                          {stop.location?.contacts?.length > 1 && (
                            <button
                              type="button"
                              className={styles.seeMore}
                              onClick={(event) => {
                                event.stopPropagation();
                                setLocationOpenSignal({ id: stop.clientId, token: Date.now() });
                              }}
                            >
                              See others
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

StopsTable.propTypes = {
  stops: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChangeStop: PropTypes.func.isRequired,
  onReorderStops: PropTypes.func.isRequired,
  onRemoveStop: PropTypes.func.isRequired,
  onLocationChange: PropTypes.func.isRequired,
  onTimeBlur: PropTypes.func.isRequired,
};

export default StopsTable;
